import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    registerPoojari,
    sendPoojariOtp,
    verifyPoojariOtp
} from '../../services/poojariService';
import { toast } from 'react-toastify';
import PasswordField from '../../components/PasswordField';
import {
    fetchPoojaTypes,
    LANGUAGES,
    DURATION_OPTIONS,
    durationValue,
    formatDuration
} from '../../utils/poojaTypes';
import { toPaise, formatPaise } from '../../utils/money';

const RED = '#B22222';
const DARK_RED = '#8B0000';
const GOLD = '#FFD700';

const RESEND_SECONDS = 30;

const emptyService = {
    poojaType: '',
    price: '',
    durationMinutes: 60,
    durationDays: 1,
    samagriAvailable: false,
    samagriPrice: ''
};

const CALL_SLOTS = [
    '9:00 am - 12:00 pm',
    '12:00 pm - 3:00 pm',
    '3:00 pm - 6:00 pm',
    '6:00 pm - 8:00 pm'
];

const getCallDates = () => {
    const dayNames = ['SUN', 'MON', 'TUE', 'WED',
        'THU', 'FRI', 'SAT'];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May',
        'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    return [1, 2].map(offset => {
        const d = new Date();
        d.setDate(d.getDate() + offset);
        return {
            value: d.toISOString().split('T')[0],
            dayName: dayNames[d.getDay()],
            dayNum: d.getDate(),
            month: monthNames[d.getMonth()],
            label: offset === 1 ? 'Tomorrow' : 'Day after'
        };
    });
};

const PoojariRegister = () => {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);

    const [otpSent, setOtpSent] = useState(false);
    const [cooldown, setCooldown] = useState(0);

    const [formData, setFormData] = useState({
        firstName: '',
        email: '',
        otp: '',
        lastName: '',
        phoneNumber: '',
        password: '',
        confirmPassword: '',
        address: '',
        city: '',
        state: '',
        pincode: '',
        experienceYears: '',
        profileDescription: '',
        verificationCallDate: '',
        verificationCallSlot: '',
        languagesKnown: []
    });

    const [services, setServices] = useState([]);
    const [newService, setNewService] = useState(emptyService);
    const [langSelect, setLangSelect] = useState('');
    const [poojaTypes, setPoojaTypes] = useState([]);

    const navigate = useNavigate();

    // Pooja types ek baar laao.
    // ⚠️ Ye PUBLIC endpoint hai — register page pe abhi token
    //    hai hi nahi, isliye yahan token wala kuch nahi chalega.
    useEffect(() => {
        let alive = true;
        fetchPoojaTypes().then(list => {
            if (alive) setPoojaTypes(list);
        });
        return () => { alive = false; };
    }, []);

    useEffect(() => {
        if (cooldown <= 0) return;
        const t = setTimeout(
            () => setCooldown(cooldown - 1), 1000);
        return () => clearTimeout(t);
    }, [cooldown]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const onlyDigits = (name, value) => {
        setFormData({
            ...formData,
            [name]: value.replace(/\D/g, '')
        });
    };

    // Email hamesha saaf karke use karo
    const cleanEmail = () =>
        formData.email.trim().toLowerCase();

    // ===== OTP =====

    const handleSendOtp = async () => {
        if (!formData.firstName.trim()) {
            return toast.warning('Please enter your first name');
        }
        if (!formData.lastName.trim()) {
            return toast.warning('Please enter your last name');
        }
        const email = cleanEmail();
        if (!/^\S+@\S+\.\S+$/.test(email)) {
            return toast.warning('Enter a valid email');
        }

        setLoading(true);
        try {
            await sendPoojariOtp(
                email, formData.firstName.trim());
            toast.success('OTP sent to your email');
            setOtpSent(true);
            setCooldown(RESEND_SECONDS);
        } catch (error) {
            toast.error(error.response?.data?.message
                || 'Could not send OTP');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async () => {
        if (formData.otp.length !== 6) {
            return toast.warning('Enter the 6 digit OTP');
        }

        setLoading(true);
        try {
            await verifyPoojariOtp(
                cleanEmail(), formData.otp.trim());
            toast.success('Email verified');
            setStep(2);
            window.scrollTo(0, 0);
        } catch (error) {
            toast.error(error.response?.data?.message
                || 'Invalid OTP');
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (cooldown > 0) return;
        setLoading(true);
        try {
            await sendPoojariOtp(
                cleanEmail(), formData.firstName.trim());
            toast.success('New OTP sent');
            setCooldown(RESEND_SECONDS);
            setFormData({ ...formData, otp: '' });
        } catch (error) {
            toast.error(error.response?.data?.message
                || 'Could not resend OTP');
        } finally {
            setLoading(false);
        }
    };

    // ===== Languages =====

    const handleLangSelect = (e) => {
        const value = e.target.value;
        setLangSelect('');
        if (!value) return;
        if (formData.languagesKnown.includes(value)) {
            return toast.info('Already added');
        }
        setFormData({
            ...formData,
            languagesKnown: [
                ...formData.languagesKnown, value]
        });
    };

    const removeLanguage = (i) => {
        setFormData({
            ...formData,
            languagesKnown: formData.languagesKnown
                .filter((_, idx) => idx !== i)
        });
    };

    // ===== Services =====

    const handleServiceChange = (e) => {
        const { name, value, type, checked } = e.target;
        setNewService({
            ...newService,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const addService = () => {
        if (!newService.poojaType) {
            return toast.warning('Select a pooja');
        }
        if (!newService.price
            || Number(newService.price) <= 0) {
            return toast.warning('Enter a valid price');
        }
        if (services.some(s =>
            s.poojaType === newService.poojaType)) {
            return toast.info('This pooja is already added');
        }
        if (newService.samagriAvailable
            && (!newService.samagriPrice
                || Number(newService.samagriPrice) <= 0)) {
            return toast.warning('Enter samagri price');
        }

        setServices([...services, {
            poojaType: newService.poojaType,
            price: parseFloat(newService.price),
            durationMinutes: parseInt(
                newService.durationMinutes),
            durationDays: parseInt(newService.durationDays) || 1,
            samagriAvailable: newService.samagriAvailable,
            samagriPrice: newService.samagriAvailable
                ? parseFloat(newService.samagriPrice) : 0
        }]);
        setNewService(emptyService);
    };

    const removeService = (i) => {
        setServices(services.filter((_, idx) => idx !== i));
    };

    // ===== Navigation =====

    const goNext = () => {
        if (step === 2) {
            if (!/^[6-9]\d{9}$/.test(formData.phoneNumber)) {
                return toast.warning(
                    'Enter a valid 10 digit mobile number');
            }
            if (formData.password.length < 8) {
                return toast.warning(
                    'Password must be at least 8 characters');
            }
            if (formData.password
                !== formData.confirmPassword) {
                return toast.warning('Passwords do not match');
            }
            if (!formData.address.trim()) {
                return toast.warning('Enter your address');
            }
            if (!formData.city.trim()
                || !formData.state.trim()) {
                return toast.warning('Enter city and state');
            }
            if (!/^\d{6}$/.test(formData.pincode)) {
                return toast.warning(
                    'Pincode must be 6 digits');
            }
            setStep(3);
            window.scrollTo(0, 0);
        }
    };

    const goBack = () => {
        if (step === 1) return navigate('/');
        if (step === 2) return;
        setStep(step - 1);
        window.scrollTo(0, 0);
    };

    const submit = async () => {
        if (!formData.experienceYears
            || Number(formData.experienceYears) < 0) {
            return toast.warning('Enter your experience');
        }
        if (formData.languagesKnown.length === 0) {
            return toast.warning('Add at least one language');
        }
        if (services.length === 0) {
            return toast.warning('Add at least one pooja');
        }
        if (!formData.verificationCallDate) {
            return toast.warning(
                'Select a date for the verification call');
        }
        if (!formData.verificationCallSlot) {
            return toast.warning('Select a time slot');
        }

        setLoading(true);
        try {
            await registerPoojari({
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim(),
                email: cleanEmail(),
                password: formData.password,
                phoneNumber: formData.phoneNumber.trim(),
                address: formData.address.trim(),
                city: formData.city.trim(),
                state: formData.state.trim(),
                pincode: formData.pincode.trim(),
                experienceYears: parseInt(
                    formData.experienceYears),
                languagesKnown: formData.languagesKnown,
                profileDescription:
                    formData.profileDescription.trim(),
                // ⚠️ Paise mein bhejna zaroori hai — form rupees
                // collect karta hai (user "500" type karta hai),
                // backend priceInPaise/samagriPriceInPaise maangta hai.
                // Field naam jaan-bujh ke badle gaye hain taaki purana
                // naam bheja jaaye to backend turant fail ho, silently
                // 100x galat na ho.
                services: services.map(s => ({
                    poojaType: s.poojaType,
                    priceInPaise: toPaise(s.price),
                    durationMinutes: s.durationMinutes,
                    durationDays: s.durationDays || 1,
                    samagriAvailable: s.samagriAvailable,
                    samagriPriceInPaise: s.samagriAvailable
                        ? toPaise(s.samagriPrice || 0)
                        : 0
                })),
                verificationCallDate:
                    formData.verificationCallDate,
                verificationCallSlot:
                    formData.verificationCallSlot
            });
            toast.success('Registration successful! '
                + 'Our team will call you at the '
                + 'selected time.');
            navigate('/poojari-login');
        } catch (error) {
            toast.error(error.response?.data?.message
                || 'Registration failed');
        } finally {
            setLoading(false);
        }
    };

    const availablePoojas = poojaTypes.filter(
        p => !services.some(s => s.poojaType === p));

    const steps = [
        { n: 1, label: 'Verify' },
        { n: 2, label: 'About' },
        { n: 3, label: 'Services' }
    ];

    const primaryAction =
        step === 1
            ? (otpSent ? handleVerifyOtp : handleSendOtp)
            : step === 2 ? goNext
                : submit;

    const primaryLabel =
        loading ? 'Please wait...'
            : step === 1
                ? (otpSent ? 'Verify' : 'Send OTP')
                : step === 2 ? 'Next'
                    : 'Register as Poojari';

    return (
        <div style={{ backgroundColor: '#FFF5F5' }}
            className="pb-screen pb-has-action-bar">

            {/* ===== HEADER ===== */}
            <div style={{
                backgroundColor: DARK_RED,
                padding: '14px 16px'
            }}>
                <div className="d-flex align-items-center gap-2">
                    {step === 1 && (
                        <button
                            onClick={goBack}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: GOLD,
                                fontSize: '22px',
                                lineHeight: 1,
                                padding: 0
                            }}>
                            ←
                        </button>
                    )}
                    <div>
                        <p className="mb-0 fw-bold"
                            style={{
                                color: GOLD,
                                fontSize: '15px'
                            }}>
                            🙏 Join as Poojari
                        </p>
                        <p className="mb-0"
                            style={{
                                color: '#FFE4B5',
                                fontSize: '12px'
                            }}>
                            Get bookings from devotees near you
                        </p>
                    </div>
                </div>
            </div>

            {/* ===== STEPS ===== */}
            <div className="pb-steps">
                {steps.map(s => (
                    <div key={s.n}
                        className={`pb-step ${step === s.n
                            ? 'active'
                            : step > s.n ? 'done' : ''}`}>
                        <div className="pb-step-bar"></div>
                        <p className="pb-step-label">
                            {s.n} · {s.label}
                        </p>
                    </div>
                ))}
            </div>

            <div className="container"
                style={{ maxWidth: '560px' }}>
                <div className="py-4">

                    {/* ============ STEP 1 ============ */}
                    {step === 1 && (
                        <>
                            <p className="text-muted mb-4"
                                style={{ fontSize: '14px' }}>
                                We will send a 6 digit code to
                                your email to verify it is
                                really you.
                            </p>

                            <div className="mb-3">
                                <label className="form-label fw-bold"
                                    style={{
                                        color: DARK_RED,
                                        fontSize: '13px'
                                    }}>
                                    First name
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="firstName"
                                    placeholder="Your first name"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    disabled={otpSent}
                                    style={{ borderColor: GOLD }}
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label fw-bold"
                                    style={{
                                        color: DARK_RED,
                                        fontSize: '13px'
                                    }}>
                                    Last name
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="lastName"
                                    placeholder="Your last name"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    disabled={otpSent}
                                    style={{ borderColor: GOLD }}
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-bold"
                                    style={{
                                        color: DARK_RED,
                                        fontSize: '13px'
                                    }}>
                                    Email address
                                </label>
                                <input
                                    type="email"
                                    className="form-control"
                                    name="email"
                                    placeholder="you@example.com"
                                    value={formData.email}
                                    onChange={handleChange}
                                    disabled={otpSent}
                                    autoComplete="email"
                                    autoCapitalize="none"
                                    spellCheck="false"
                                    style={{ borderColor: GOLD }}
                                />
                            </div>

                            {otpSent && (
                                <>
                                    <div className="mb-3">
                                        <label className="form-label fw-bold"
                                            style={{
                                                color: DARK_RED,
                                                fontSize: '13px'
                                            }}>
                                            Enter OTP
                                        </label>
                                        <input
                                            type="tel"
                                            className="form-control text-center"
                                            placeholder="000000"
                                            value={formData.otp}
                                            maxLength="6"
                                            autoFocus
                                            inputMode="numeric"
                                            onChange={e =>
                                                onlyDigits('otp',
                                                    e.target.value)}
                                            style={{
                                                borderColor: GOLD,
                                                fontSize: '26px',
                                                letterSpacing: '10px',
                                                fontWeight: 700,
                                                color: DARK_RED
                                            }}
                                        />
                                    </div>

                                    <div className="text-center">
                                        {cooldown > 0 ? (
                                            <small className="text-muted">
                                                Resend code in{' '}
                                                {cooldown}s
                                            </small>
                                        ) : (
                                            <button
                                                className="btn btn-sm p-0"
                                                style={{
                                                    color: RED,
                                                    border: 'none',
                                                    background: 'none',
                                                    fontSize: '14px',
                                                    minHeight: 'auto',
                                                    fontWeight: 600
                                                }}
                                                onClick={handleResend}
                                                disabled={loading}>
                                                Resend code
                                            </button>
                                        )}
                                    </div>

                                    <div className="p-3 mt-4"
                                        style={{
                                            backgroundColor:
                                                '#FFF8F0',
                                            borderRadius: '10px'
                                        }}>
                                        <small className="text-muted">
                                            Code not in your inbox?
                                            Check the spam folder.
                                            It expires in 10 minutes.
                                        </small>
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {/* ============ STEP 2 ============ */}
                    {step === 2 && (
                        <>
                            <div className="p-3 mb-4"
                                style={{
                                    backgroundColor: '#EAF3DE',
                                    borderRadius: '10px'
                                }}>
                                <small style={{
                                    color: '#27500A'
                                }}>
                                    ✓ {cleanEmail()} verified
                                </small>
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-bold"
                                    style={{
                                        color: DARK_RED,
                                        fontSize: '13px'
                                    }}>
                                    Mobile number
                                </label>
                                <input
                                    type="tel"
                                    className="form-control"
                                    placeholder="10 digit number"
                                    value={formData.phoneNumber}
                                    maxLength="10"
                                    inputMode="numeric"
                                    onChange={e =>
                                        onlyDigits('phoneNumber',
                                            e.target.value)}
                                    style={{ borderColor: GOLD }}
                                />
                                <small className="text-muted">
                                    Devotees will call you on this
                                </small>
                            </div>

                            <div className="mb-3">
                                <PasswordField
                                    label="Password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    placeholder="Min 8 characters"
                                />
                            </div>

                            <div className="mb-4">
                                <PasswordField
                                    label="Confirm password"
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    placeholder="Type it again"
                                />
                            </div>

                            <hr />

                            <p className="fw-bold mb-3"
                                style={{
                                    color: DARK_RED,
                                    fontSize: '14px'
                                }}>
                                Where are you based
                            </p>

                            <div className="mb-3">
                                <label className="form-label fw-bold"
                                    style={{
                                        color: DARK_RED,
                                        fontSize: '13px'
                                    }}>
                                    Address
                                </label>
                                <textarea
                                    className="form-control"
                                    name="address"
                                    rows="2"
                                    placeholder="House no, street, area"
                                    value={formData.address}
                                    onChange={handleChange}
                                    style={{ borderColor: GOLD }}>
                                </textarea>
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-bold"
                                    style={{
                                        color: DARK_RED,
                                        fontSize: '13px'
                                    }}>
                                    City
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="city"
                                    placeholder="City"
                                    value={formData.city}
                                    onChange={handleChange}
                                    style={{ borderColor: GOLD }}
                                />
                            </div>

                            <div className="row g-2 mb-3">
                                <div className="col-7">
                                    <label className="form-label fw-bold"
                                        style={{
                                            color: DARK_RED,
                                            fontSize: '13px'
                                        }}>
                                        State
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="state"
                                        placeholder="State"
                                        value={formData.state}
                                        onChange={handleChange}
                                        style={{ borderColor: GOLD }}
                                    />
                                </div>
                                <div className="col-5">
                                    <label className="form-label fw-bold"
                                        style={{
                                            color: DARK_RED,
                                            fontSize: '13px'
                                        }}>
                                        Pincode
                                    </label>
                                    <input
                                        type="tel"
                                        className="form-control"
                                        placeholder="751001"
                                        maxLength="6"
                                        inputMode="numeric"
                                        value={formData.pincode}
                                        onChange={e =>
                                            onlyDigits('pincode',
                                                e.target.value)}
                                        style={{ borderColor: GOLD }}
                                    />
                                    <small className="text-muted">
                                        Helps nearby devotees
                                        find you
                                    </small>
                                </div>
                            </div>
                        </>
                    )}

                    {/* ============ STEP 3 ============ */}
                    {step === 3 && (
                        <>
                            <div className="row g-2 mb-3">
                                <div className="col-5">
                                    <label className="form-label fw-bold"
                                        style={{
                                            color: DARK_RED,
                                            fontSize: '13px'
                                        }}>
                                        Experience
                                    </label>
                                    <input
                                        type="tel"
                                        className="form-control"
                                        placeholder="Years"
                                        maxLength="2"
                                        inputMode="numeric"
                                        value={formData.experienceYears}
                                        onChange={e =>
                                            onlyDigits(
                                                'experienceYears',
                                                e.target.value)}
                                        style={{ borderColor: GOLD }}
                                    />
                                </div>
                                <div className="col-7">
                                    <label className="form-label fw-bold"
                                        style={{
                                            color: DARK_RED,
                                            fontSize: '13px'
                                        }}>
                                        Languages
                                    </label>
                                    <select
                                        className="form-select"
                                        value={langSelect}
                                        onChange={handleLangSelect}
                                        style={{ borderColor: GOLD }}>
                                        <option value="">
                                            Add a language
                                        </option>
                                        {LANGUAGES.map(l => (
                                            <option key={l} value={l}>
                                                {l}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {formData.languagesKnown.length > 0 && (
                                <div className="mb-4">
                                    {formData.languagesKnown.map(
                                        (lang, i) => (
                                            <span key={i}
                                                className="badge bg-secondary me-2 mb-1 p-2">
                                                {lang}
                                                <button
                                                    type="button"
                                                    className="btn-close btn-close-white ms-2"
                                                    style={{
                                                        fontSize: '0.6rem'
                                                    }}
                                                    onClick={() =>
                                                        removeLanguage(i)}>
                                                </button>
                                            </span>
                                        ))}
                                </div>
                            )}

                            <hr />

                            <p className="fw-bold mb-1"
                                style={{
                                    color: DARK_RED,
                                    fontSize: '14px'
                                }}>
                                Poojas you perform
                            </p>
                            <p className="text-muted mb-3"
                                style={{ fontSize: '13px' }}>
                                Add each pooja with your fees and
                                how long it takes
                            </p>

                            {services.length > 0 && (
                                <div className="mb-3"
                                    style={{
                                        border: `1px solid ${GOLD}`,
                                        borderRadius: '10px',
                                        overflow: 'hidden'
                                    }}>
                                    {services.map((s, i) => (
                                        <div key={i}
                                            className="d-flex justify-content-between align-items-center p-3"
                                            style={{
                                                borderBottom:
                                                    i < services.length - 1
                                                        ? '1px solid #eee'
                                                        : 'none',
                                                backgroundColor:
                                                    '#FFF8F8'
                                            }}>
                                            <div>
                                                <strong style={{
                                                    color: DARK_RED,
                                                    fontSize: '14px'
                                                }}>
                                                    {s.poojaType}
                                                </strong>
                                                <div className="text-muted"
                                                    style={{
                                                        fontSize: '12px'
                                                    }}>
                                                    {formatPaise(toPaise(s.price))} ·{' '}
                                                    {formatDuration(
                                                        s.durationMinutes, s.durationDays)}
                                                    {s.samagriAvailable
                                                        ? ` · samagri +${formatPaise(toPaise(s.samagriPrice))}`
                                                        : ''}
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-danger"
                                                onClick={() =>
                                                    removeService(i)}>
                                                Remove
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="p-3 mb-3"
                                style={{
                                    backgroundColor: '#FFF5F5',
                                    borderRadius: '10px',
                                    border: '1px dashed #ccc'
                                }}>
                                <div className="mb-2">
                                    <label className="form-label"
                                        style={{ fontSize: '12px' }}>
                                        Pooja
                                    </label>
                                    <select
                                        className="form-select"
                                        name="poojaType"
                                        value={newService.poojaType}
                                        onChange={
                                            handleServiceChange}>
                                        <option value="">
                                            Select pooja
                                        </option>
                                        {availablePoojas.map(p => (
                                            <option key={p} value={p}>
                                                {p}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="row g-2">
                                    <div className="col-5">
                                        <label className="form-label"
                                            style={{
                                                fontSize: '12px'
                                            }}>
                                            Your fees (₹)
                                        </label>
                                        <input
                                            type="tel"
                                            className="form-control"
                                            name="price"
                                            placeholder="500"
                                            inputMode="numeric"
                                            value={newService.price}
                                            onChange={
                                                handleServiceChange}
                                        />
                                    </div>
                                    <div className="col-7">
                                        <label className="form-label"
                                            style={{
                                                fontSize: '12px'
                                            }}>
                                            Duration
                                        </label>
                                        <select
                                            className="form-select"
                                            value={durationValue(
                                                newService.durationMinutes,
                                                newService.durationDays)}
                                            onChange={e => {
                                                // ⚠️ Value "minutes_days" hai -
                                                //    HTML select ek hi value rakh
                                                //    sakta hai, par hamein do
                                                //    chahiye.
                                                const [m, d] = e.target.value.split('_');
                                                setNewService({
                                                    ...newService,
                                                    durationMinutes: parseInt(m),
                                                    durationDays: parseInt(d)
                                                });
                                            }}>
                                            {DURATION_OPTIONS.map(d => (
                                                <option key={d.label}
                                                    value={durationValue(d.minutes, d.days)}>
                                                    {d.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <label className="d-flex align-items-center gap-2 mt-3"
                                    style={{
                                        fontSize: '14px',
                                        cursor: 'pointer'
                                    }}>
                                    <input
                                        type="checkbox"
                                        name="samagriAvailable"
                                        checked={
                                            newService.samagriAvailable}
                                        onChange={
                                            handleServiceChange}
                                    />
                                    <span style={{
                                        color: DARK_RED
                                    }}>
                                        I can arrange samagri
                                    </span>
                                </label>

                                {newService.samagriAvailable && (
                                    <div className="mt-2">
                                        <label className="form-label"
                                            style={{
                                                fontSize: '12px'
                                            }}>
                                            Samagri charge (₹)
                                        </label>
                                        <input
                                            type="tel"
                                            className="form-control"
                                            name="samagriPrice"
                                            placeholder="300"
                                            inputMode="numeric"
                                            value={
                                                newService.samagriPrice}
                                            onChange={
                                                handleServiceChange}
                                        />
                                    </div>
                                )}

                                <button
                                    type="button"
                                    className="btn w-100 mt-3 fw-bold"
                                    style={{
                                        backgroundColor: GOLD,
                                        color: DARK_RED,
                                        border: 'none'
                                    }}
                                    onClick={addService}>
                                    + Add this pooja
                                </button>
                            </div>

                            <div className="mb-4">
                                <label className="form-label fw-bold"
                                    style={{
                                        color: DARK_RED,
                                        fontSize: '13px'
                                    }}>
                                    About you
                                </label>
                                <textarea
                                    className="form-control"
                                    name="profileDescription"
                                    rows="3"
                                    placeholder="Tell devotees about your experience and approach..."
                                    value={
                                        formData.profileDescription}
                                    onChange={handleChange}
                                    style={{ borderColor: GOLD }}>
                                </textarea>
                            </div>

                            <hr />

                            {/* ===== VERIFICATION CALL ===== */}
                            <p className="fw-bold mb-1"
                                style={{
                                    color: DARK_RED,
                                    fontSize: '14px'
                                }}>
                                Verification call
                            </p>
                            <p className="text-muted mb-3"
                                style={{ fontSize: '13px' }}>
                                Our team will call you to verify
                                your profile. Pick a time that
                                works for you.
                            </p>

                            <label className="form-label fw-bold"
                                style={{
                                    color: DARK_RED,
                                    fontSize: '13px'
                                }}>
                                Which day
                            </label>
                            <div className="d-flex gap-2 mb-3">
                                {getCallDates().map(d => (
                                    <div key={d.value}
                                        onClick={() =>
                                            setFormData({
                                                ...formData,
                                                verificationCallDate:
                                                    d.value
                                            })}
                                        className="flex-fill text-center"
                                        style={{
                                            padding: '12px 8px',
                                            borderRadius: '12px',
                                            cursor: 'pointer',
                                            border:
                                                formData.verificationCallDate
                                                    === d.value
                                                    ? `2px solid ${RED}`
                                                    : '1px solid #ddd',
                                            backgroundColor:
                                                formData.verificationCallDate
                                                    === d.value
                                                    ? '#FFF5F5' : 'white'
                                        }}>
                                        <p className="mb-0"
                                            style={{
                                                fontSize: '11px',
                                                color: '#999'
                                            }}>
                                            {d.label.toUpperCase()}
                                        </p>
                                        <p className="mb-0 fw-bold"
                                            style={{
                                                fontSize: '16px',
                                                color: DARK_RED
                                            }}>
                                            {d.dayNum} {d.month}
                                        </p>
                                        <p className="mb-0"
                                            style={{
                                                fontSize: '11px',
                                                color: '#999'
                                            }}>
                                            {d.dayName}
                                        </p>
                                    </div>
                                ))}
                            </div>

                            <label className="form-label fw-bold"
                                style={{
                                    color: DARK_RED,
                                    fontSize: '13px'
                                }}>
                                What time
                            </label>
                            <div className="pb-slot-grid mb-3"
                                style={{
                                    gridTemplateColumns:
                                        'repeat(2, 1fr)'
                                }}>
                                {CALL_SLOTS.map(slot => (
                                    <div key={slot}
                                        className={`pb-slot ${formData.verificationCallSlot
                                            === slot
                                            ? 'selected' : ''}`}
                                        onClick={() =>
                                            setFormData({
                                                ...formData,
                                                verificationCallSlot:
                                                    slot
                                            })}
                                        style={{
                                            fontSize: '12px'
                                        }}>
                                        {slot}
                                    </div>
                                ))}
                            </div>

                            <div className="p-3"
                                style={{
                                    backgroundColor: '#FFF8F0',
                                    borderRadius: '10px'
                                }}>
                                <small className="text-muted">
                                    Keep your phone handy at the
                                    chosen time. Once verified,
                                    you can start accepting
                                    bookings.
                                </small>
                            </div>
                        </>
                    )}

                    <div className="text-center mt-4">
                        <small className="text-muted">
                            Already registered?{' '}
                            <Link to="/poojari-login"
                                style={{
                                    color: RED,
                                    fontWeight: 600
                                }}>
                                Login
                            </Link>
                        </small>
                    </div>
                </div>
            </div>

            {/* ===== STICKY ACTION BAR ===== */}
            <div className="pb-action-bar">
                <div>
                    <small className="text-muted">
                        Step {step} of 3
                    </small>
                    {step === 3 && services.length > 0 && (
                        <p className="mb-0 fw-bold"
                            style={{
                                fontSize: '13px',
                                color: DARK_RED
                            }}>
                            {services.length} pooja(s) added
                        </p>
                    )}
                </div>
                <button
                    className="btn fw-bold px-4"
                    style={{
                        backgroundColor: RED,
                        color: GOLD,
                        border: 'none',
                        borderRadius: '10px'
                    }}
                    disabled={loading}
                    onClick={primaryAction}>
                    {primaryLabel}
                </button>
            </div>
        </div>
    );
};

export default PoojariRegister;